import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { Branch } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';

@Component({
  selector: 'app-branches',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './branches.component.html',
  styleUrls: ['../../settings-shared.css', './branches.component.css'],
})
export class BranchesComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');
  protected readonly toastIsError = signal(false);

  protected branchForm: Omit<Branch, 'id'> = {
    code: '',
    name: '',
    city: '',
    status: 'active',
  };

  protected readonly branches = this.settings.companyBranches;

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Branch): void {
    this.editingId.set(row.id);
    this.branchForm = {
      code: row.code,
      name: row.name,
      city: row.city,
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.branchForm.code || !this.branchForm.name) {
      this.toastMessage.set('Code and Name are required');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (editingId) {
      if (!this.settings.updateBranch(editingId, this.branchForm)) {
        this.toastMessage.set(
          'Branch update is not available via the API yet.',
        );
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 4000);
        return;
      }
      this.toastMessage.set('Branch updated successfully');
      this.closeModal();
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    this.settings.addBranch(this.branchForm).subscribe((result) => {
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeModal();
      }
    });
  }

  deleteRow(row: Branch): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    this.settings.deleteBranch(row.id).subscribe((result) => {
      this.showNotice(result.message, !result.success);
    });
  }

  private resetForm(): void {
    this.branchForm = {
      code: '',
      name: '',
      city: '',
      status: 'active',
    };
  }

  private showNotice(message: string, isError = false): void {
    this.toastIsError.set(isError);
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }
}
